import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useLocation} from 'react-router-dom';
import styled from 'styled-components/macro';
import theme from 'styled-theming';

import {authService} from '@services/auth.service';
import useTutorialTracker from '@hooks/useTutorialTracker';
import {getModuleTour} from '@constants/moduleTours';
import {colors, dark, light, textSizes} from '@styles/vars';

const TOUR_Z_INDEX = 22000000;

const OverlayRoot = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${TOUR_Z_INDEX};
    pointer-events: none;
`;

const ScreenShade = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(9, 18, 30, 0.58);
`;

const Spotlight = styled.div`
    position: fixed;
    z-index: ${TOUR_Z_INDEX + 1};
    border-radius: 16px;
    border: 2px solid rgba(255, 255, 255, 0.92);
    box-shadow: 0 0 0 9999px rgba(9, 18, 30, 0.58);
    transition: top 0.18s ease, left 0.18s ease, width 0.18s ease, height 0.18s ease;
    pointer-events: none;
`;

const TooltipCard = styled.div`
    position: fixed;
    z-index: ${TOUR_Z_INDEX + 10};
    width: min(360px, calc(100vw - 32px));
    max-height: min(38vh, 300px);
    overflow-y: auto;
    border-radius: 18px;
    padding: 18px;
    box-sizing: border-box;
    background: ${theme('theme', {
        light: '#F8FBFF',
        dark: '#132033'
    })};
    color: ${theme('theme', {
        light: light.text,
        dark: dark.text
    })};
    border: 1px solid ${theme('theme', {
        light: '#B9D0E4',
        dark: dark.border
    })};
    box-shadow: 0 18px 48px rgba(6, 16, 30, 0.28);
    pointer-events: auto;
    isolation: isolate;

    ${props => props.$docked ? `
        top: auto !important;
        bottom: max(20px, env(safe-area-inset-bottom, 0px));
        left: 50% !important;
        right: auto;
        transform: translateX(-50%);
    ` : ''}

    h4 {
        margin: 0 0 8px;
        font-size: ${textSizes['18']};
    }

    p {
        margin: 0;
        font-size: ${textSizes['14']};
        line-height: 1.6;
        opacity: 0.92;
    }
`;

const StepMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
    font-size: ${textSizes['12']};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${colors.blue};
    font-weight: 700;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;
    margin-top: 16px;
`;

const ActionGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const ActionButton = styled.button`
    min-height: 38px;
    padding: 0 14px;
    border-radius: 10px;
    border: 1px solid ${props => props.$primary ? colors.blue : theme('theme', {
        light: '#B9D0E4',
        dark: dark.border
    })};
    background: ${props => props.$primary ? colors.blue : 'transparent'};
    color: ${props => props.$primary ? '#fff' : 'inherit'};
    font-size: ${textSizes['13']};
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const ReplayFab = styled.button`
    position: fixed;
    bottom: max(24px, env(safe-area-inset-bottom, 0px));
    right: 24px;
    z-index: ${TOUR_Z_INDEX - 1};
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: none;
    background: ${colors.blue};
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 90, 200, 0.32);
    opacity: 0.85;
    transition: opacity 0.15s, transform 0.15s;
    pointer-events: auto;

    &:hover {
        opacity: 1;
        transform: scale(1.1);
    }
`;

const resolveElement = selector => (selector ? document.querySelector(selector) : null);

const MARGIN = 16;
const GAP = 12;
const TOOLTIP_MAX_WIDTH = 360;
const TOOLTIP_HEIGHT_GUESS = 220;
const MAX_SPOTLIGHT_HEIGHT_RATIO = 0.5;
const ULTRAWIDE_ASPECT_RATIO = 2.1;
const ULTRAWIDE_MIN_WIDTH = 2560;
const LOW_VIEWPORT_HEIGHT = 820;

/**
 * position:fixed es relativo al layout viewport del navegador.
 * No usar visualViewport.offsetTop/Left: en monitor ultrawide/secundario desplaza la burbuja fuera de pantalla.
 */
const getViewport = () => {
    const layoutWidth = window.innerWidth;
    const layoutHeight = window.innerHeight;
    const vv = window.visualViewport;
    const keyboardOpen = vv && layoutHeight - vv.height > 72;

    return {
        width: keyboardOpen ? vv.width : layoutWidth,
        height: keyboardOpen ? vv.height : layoutHeight,
        layoutWidth,
        layoutHeight
    };
};

const shouldUseDockedTooltip = viewport => {
    const aspect = viewport.width / Math.max(viewport.height, 1);
    return aspect >= ULTRAWIDE_ASPECT_RATIO || viewport.width >= ULTRAWIDE_MIN_WIDTH || viewport.height <= LOW_VIEWPORT_HEIGHT;
};

const resolveDefaultPageSelector = pathname => {
    const pageRoot = document.querySelector(`[data-tour-page="${pathname}"]`);
    if (!pageRoot) {
        return `[data-tour-page="${pathname}"]`;
    }

    const widgets = pageRoot.querySelectorAll('[data-widget]');
    if (widgets.length >= 1 && !pathname.startsWith('/dashboard')) {
        const widgetName = widgets[0].getAttribute('data-widget');
        return `[data-tour-page="${pathname}"] [data-widget="${widgetName}"]`;
    }

    return `[data-tour-page="${pathname}"]`;
};

const expandSidebarAccordionFor = element => {
    const accordionItem = element?.closest?.('.accordion-item');
    if (!accordionItem) {
        return;
    }

    const button = accordionItem.querySelector('.accordion-button');
    if (button?.classList?.contains('collapsed')) {
        button.click();
    }
};

const ensureSidebarLinkVisible = linkPath => {
    if (!linkPath) {
        return null;
    }

    let link = resolveElement(`[data-tour-link="${linkPath}"]`);
    if (link) {
        return link;
    }

    const category =
        linkPath.startsWith('/inventory') ? 'Inventario'
        : linkPath.startsWith('/crm') ? 'CRM'
        : linkPath.startsWith('/movements') ? 'Movimientos'
        : linkPath.startsWith('/agenda') ? 'Agenda'
        : linkPath.startsWith('/patients') || linkPath.startsWith('/studies') || linkPath.startsWith('/historia-clinica') ? 'Pacientes'
        : linkPath.startsWith('/settings') ? 'Configuracion'
        : null;

    if (category) {
        expandSidebarAccordionFor(resolveElement(`[data-tour-sidebar="${category}"]`));
    }

    return resolveElement(`[data-tour-link="${linkPath}"]`);
};

const getScrollParent = element => {
    let parent = element?.parentElement;

    while (parent) {
        const {overflowY} = window.getComputedStyle(parent);
        if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight + 1) {
            return parent;
        }
        parent = parent.parentElement;
    }

    return document.querySelector('.app_content') || null;
};

const scrollTargetIntoView = element => {
    if (!element) {
        return;
    }

    element.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'auto'});

    const scrollParent = getScrollParent(element);
    if (!scrollParent) {
        return;
    }

    const elementRect = element.getBoundingClientRect();
    const parentRect = scrollParent.getBoundingClientRect();
    const padding = 16;

    if (elementRect.top < parentRect.top + padding) {
        scrollParent.scrollTop -= parentRect.top - elementRect.top + padding;
    } else if (elementRect.bottom > parentRect.bottom - padding) {
        scrollParent.scrollTop += elementRect.bottom - parentRect.bottom + padding;
    }
};

const buildStepRect = (element, dockedTooltip = false) => {
    if (!element) {
        return null;
    }

    scrollTargetIntoView(element);

    const bounds = element.getBoundingClientRect();
    if (bounds.width < 1 && bounds.height < 1) {
        return null;
    }

    const viewport = getViewport();
    const padding = 8;
    const spotlightZone = dockedTooltip ? 0.58 : MAX_SPOTLIGHT_HEIGHT_RATIO;
    const maxH = Math.max(viewport.height * spotlightZone, 64);
    const maxBottom = dockedTooltip
        ? viewport.height * 0.58
        : viewport.height - MARGIN;

    let top = Math.max(bounds.top - padding, MARGIN);
    let left = Math.max(bounds.left - padding, MARGIN);
    let width = Math.min(bounds.width + padding * 2, viewport.width - MARGIN * 2);
    let height = Math.min(bounds.height + padding * 2, maxH);

    if (top + height > maxBottom) {
        height = Math.min(height, Math.max(maxBottom - top, 48));
    }

    if (dockedTooltip && bounds.left > viewport.width * 0.45) {
        left = Math.max(MARGIN, Math.min(left, viewport.width * 0.35));
        width = Math.min(width, viewport.width * 0.32);
    }

    return {top, left, width, height};
};

const buildTooltipPosition = (spotlightRect, tooltipHeight = TOOLTIP_HEIGHT_GUESS, docked = false) => {
    if (docked) {
        return {top: null, left: null, docked: true};
    }

    const viewport = getViewport();
    const cardWidth = Math.min(TOOLTIP_MAX_WIDTH, viewport.width - MARGIN * 2);
    const cardHeight = Math.min(tooltipHeight, viewport.height - MARGIN * 2);
    const minTop = MARGIN;
    const minLeft = MARGIN;
    const maxTop = viewport.height - cardHeight - MARGIN;
    const maxLeft = viewport.width - cardWidth - MARGIN;

    const clampPosition = ({top, left}) => ({
        top: Math.max(minTop, Math.min(top, maxTop)),
        left: Math.max(minLeft, Math.min(left, maxLeft)),
        docked: false
    });

    const centerInViewport = () =>
        clampPosition({
            top: (viewport.height - cardHeight) / 2,
            left: (viewport.width - cardWidth) / 2
        });

    const dockBottomCenter = () => ({top: null, left: null, docked: true});

    if (!spotlightRect) {
        return dockBottomCenter();
    }

    const candidates = [
        {
            top: spotlightRect.top + spotlightRect.height + GAP,
            left: spotlightRect.left + spotlightRect.width / 2 - cardWidth / 2
        },
        {
            top: spotlightRect.top - cardHeight - GAP,
            left: spotlightRect.left + spotlightRect.width / 2 - cardWidth / 2
        },
        dockBottomCenter(),
        centerInViewport()
    ];

    const fits = ({top, left}) =>
        top >= minTop &&
        left >= minLeft &&
        top + cardHeight <= viewport.offsetTop + viewport.height - MARGIN &&
        left + cardWidth <= viewport.offsetLeft + viewport.width - MARGIN;

    return clampPosition(candidates.find(fits) || candidates[candidates.length - 1]);
};

const resolveTourTarget = (selector, moduleTour, stepKind = 'default') => {
    if (!selector) {
        return null;
    }

    if (stepKind === 'intro' && moduleTour?.sidebarLink) {
        return ensureSidebarLinkVisible(moduleTour.sidebarLink);
    }

    if (stepKind === 'intro' && moduleTour?.sidebar) {
        const header = resolveElement(`[data-tour-sidebar="${moduleTour.sidebar}"]`);
        if (header) {
            expandSidebarAccordionFor(header);
            return header;
        }
    }

    const element = resolveElement(selector);
    if (element) {
        expandSidebarAccordionFor(element);
    }

    return element;
};

const AppModuleTour = () => {
    const {pathname} = useLocation();
    const {tutoriales, cargando, marcarComoCompletado, reiniciarTutorial} = useTutorialTracker();
    const [steps, setSteps] = useState([]);
    const [index, setIndex] = useState(0);
    const [active, setActive] = useState(false);
    const [spotlightRect, setSpotlightRect] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({top: MARGIN, left: MARGIN, docked: false});
    const tooltipRef = useRef(null);
    const sessionDismissedRef = useRef(new Set());

    const moduleTour = useMemo(() => getModuleTour(pathname), [pathname]);
    const currentStep = steps[index] || null;
    const tutorialCompletado = moduleTour ? tutoriales?.[moduleTour.key] === true : false;
    const tenantReady = Boolean(authService.getTenantId());

    const updateLayout = useCallback(() => {
        if (!active || !currentStep) {
            return;
        }

        const viewport = getViewport();
        const docked = shouldUseDockedTooltip(viewport);
        const element = resolveTourTarget(
            currentStep.selector,
            moduleTour,
            currentStep.kind
        );
        const rect = buildStepRect(element, docked);
        const tooltipHeight = tooltipRef.current?.offsetHeight || TOOLTIP_HEIGHT_GUESS;

        setSpotlightRect(rect);
        setTooltipPosition(buildTooltipPosition(rect, tooltipHeight, docked));
    }, [active, currentStep, moduleTour]);

    useEffect(() => {
        if (!moduleTour || !authService.isAuthenticated() || !tenantReady || cargando || tutoriales === null) {
            setActive(false);
            setSteps([]);
            setIndex(0);
            return;
        }

        if (tutorialCompletado || sessionDismissedRef.current.has(moduleTour.key)) {
            setActive(false);
            setSteps([]);
            setIndex(0);
            return;
        }

        let cancelled = false;
        let attempt = 0;

        const hydrateSteps = () => {
            if (cancelled) {
                return;
            }

            if (moduleTour.sidebarLink) {
                ensureSidebarLinkVisible(moduleTour.sidebarLink);
            }

            const pageSelector = moduleTour.pageSelector || resolveDefaultPageSelector(pathname);

            const rawSteps = [
                {
                    kind: 'intro',
                    selector: moduleTour.sidebarLink
                        ? `[data-tour-link="${moduleTour.sidebarLink}"]`
                        : `[data-tour-sidebar="${moduleTour.sidebar}"]`,
                    title: moduleTour.introTitle,
                    description: moduleTour.introDescription
                },
                {
                    kind: 'page',
                    selector: pageSelector,
                    title: moduleTour.pageTitle,
                    description: moduleTour.pageDescription
                },
                ...(moduleTour.steps || []).map(step => ({...step, kind: 'feature'}))
            ];

            const nextSteps = rawSteps
                .map(step => ({
                    ...step,
                    element: resolveTourTarget(step.selector, moduleTour, step.kind)
                }))
                .filter(step => !step.selector || step.element);

            if (nextSteps.length < 2 && attempt < 12) {
                attempt += 1;
                window.setTimeout(hydrateSteps, 200);
                return;
            }

            setSteps(nextSteps);
            setIndex(0);
            setActive(nextSteps.length > 0);
        };

        window.setTimeout(hydrateSteps, 300);

        return () => {
            cancelled = true;
        };
    }, [moduleTour, pathname, cargando, tutoriales, tutorialCompletado, tenantReady]);

    useEffect(() => {
        if (!active || !currentStep) {
            return undefined;
        }

        let frameId = 0;
        const schedule = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(updateLayout);
        };

        schedule();
        const t1 = window.setTimeout(schedule, 80);
        const t2 = window.setTimeout(schedule, 280);

        window.addEventListener('resize', schedule);
        window.addEventListener('scroll', schedule, true);
        window.visualViewport?.addEventListener('resize', schedule);
        window.visualViewport?.addEventListener('scroll', schedule);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            window.removeEventListener('resize', schedule);
            window.removeEventListener('scroll', schedule, true);
            window.visualViewport?.removeEventListener('resize', schedule);
            window.visualViewport?.removeEventListener('scroll', schedule);
        };
    }, [active, currentStep, index, updateLayout]);

    useLayoutEffect(() => {
        if (!active || !tooltipRef.current) {
            return;
        }

        const tooltipHeight = tooltipRef.current.offsetHeight || TOOLTIP_HEIGHT_GUESS;
        const docked = shouldUseDockedTooltip(getViewport());
        setTooltipPosition(prev => {
            const next = buildTooltipPosition(spotlightRect, tooltipHeight, docked);
            if (prev.top === next.top && prev.left === next.left && prev.docked === next.docked) {
                return prev;
            }
            return next;
        });
    }, [active, index, spotlightRect, currentStep?.title]);

    const closeTour = () => {
        if (moduleTour) {
            sessionDismissedRef.current.add(moduleTour.key);
            marcarComoCompletado(moduleTour.key).catch(() => {});
        }

        setActive(false);
        setSteps([]);
        setIndex(0);
        setSpotlightRect(null);
    };

    const nextStep = () => {
        if (index >= steps.length - 1) {
            closeTour();
            return;
        }

        setIndex(prev => prev + 1);
    };

    const previousStep = () => {
        setIndex(prev => Math.max(prev - 1, 0));
    };

    const replayTour = () => {
        if (!moduleTour) {
            return;
        }

        sessionDismissedRef.current.delete(moduleTour.key);

        reiniciarTutorial(moduleTour.key)
            .catch(() => {})
            .finally(() => {
                setActive(false);
                setSteps([]);
                setIndex(0);
            });
    };

    const showFab = !active && moduleTour && tenantReady && !cargando && tutoriales !== null && authService.isAuthenticated();

    if (!active || !currentStep) {
        if (!showFab) {
            return null;
        }

        return createPortal(
            <ReplayFab
                type="button"
                title="Ver tutorial del módulo"
                aria-label="Ver tutorial del módulo"
                onClick={replayTour}
            >
                ?
            </ReplayFab>,
            document.body
        );
    }

    const tooltipStyle = tooltipPosition.docked
        ? undefined
        : {
            top: tooltipPosition.top,
            left: tooltipPosition.left
        };

    return createPortal(
        <>
            <OverlayRoot>
                {spotlightRect ? (
                    <Spotlight
                        style={{
                            top: spotlightRect.top,
                            left: spotlightRect.left,
                            width: spotlightRect.width,
                            height: spotlightRect.height
                        }}
                    />
                ) : (
                    <ScreenShade />
                )}
            </OverlayRoot>
            <TooltipCard ref={tooltipRef} $docked={tooltipPosition.docked} style={tooltipStyle}>
                <StepMeta>
                    <span>Tutorial guiado</span>
                    <span>{index + 1} / {steps.length}</span>
                </StepMeta>
                <h4>{currentStep.title}</h4>
                <p>{currentStep.description}</p>
                <Actions>
                    <ActionButton type="button" onClick={closeTour}>Omitir</ActionButton>
                    <ActionGroup>
                        <ActionButton type="button" onClick={previousStep} disabled={index === 0}>
                            Anterior
                        </ActionButton>
                        <ActionButton type="button" $primary={1} onClick={nextStep}>
                            {index === steps.length - 1 ? 'Finalizar' : 'Siguiente'}
                        </ActionButton>
                    </ActionGroup>
                </Actions>
            </TooltipCard>
        </>,
        document.body
    );
};

export default AppModuleTour;
